import { Immutable } from "immer";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { CSRF_HEADER, fetchJson, getCSRFToken } from "../utils/ajax";

/**
 * Decode a base64url-encoded string (as used in JWTs) to a UTF-8 string.
 * JWTs use base64url encoding which replaces `+` with `-` and `/` with `_`,
 * and omits padding. `atob()` only handles standard base64, so we convert first.
 */
const base64UrlDecode = (str: string): string => {
    const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const padded = pad ? base64 + "=".repeat(4 - pad) : base64;
    return atob(padded);
};

/**
 * Decode and JSON-parse the payload (second) segment of a JWT. Returns
 * `undefined` for anything that isn't a decodable three-segment token, leaving
 * each caller to decide whether that is a hard failure or a best-effort miss.
 */
const decodeJwtPayload = <T>(jwt: string): T | undefined => {
    const parts = jwt.split(".");
    if (parts.length !== 3) {
        return undefined;
    }
    try {
        return JSON.parse(base64UrlDecode(parts[1])) as T;
    } catch {
        return undefined;
    }
};

type CaptureContextPayload = Immutable<{
    ctx: Array<{
        data: {
            clientLibrary: string;
            clientLibraryIntegrity: string;
        };
    }>;
}>;

type ICaptureContextResponse = Immutable<{
    capture_context: string;
}>;

/**
 * Decode the capture context JWT payload to extract the Flex SDK URL and SRI hash.
 * Only decodes the payload section; no signature verification (backend already verified).
 */
export const decodeCaptureContextJwt = (
    jwt: string,
): { clientLibrary: string; clientLibraryIntegrity: string } => {
    const payload = decodeJwtPayload<CaptureContextPayload>(jwt);
    if (payload === undefined) {
        throw new Error("Invalid capture context JWT format");
    }
    const ctx = payload.ctx[0]?.data;
    if (!ctx || !ctx.clientLibrary || !ctx.clientLibraryIntegrity) {
        throw new Error(
            "Capture context JWT missing clientLibrary or clientLibraryIntegrity",
        );
    }
    return {
        clientLibrary: ctx.clientLibrary,
        clientLibraryIntegrity: ctx.clientLibraryIntegrity,
    };
};

type TransientTokenPayload = Immutable<{
    iat?: number;
    exp?: number;
    content?: {
        paymentInformation?: {
            card?: {
                number?: {
                    maskedValue?: string;
                    detectedCardTypes?: string[];
                };
            };
        };
    };
}>;

/**
 * Decode the card metadata (masked card number and CyberSource card type) from
 * a transient token JWT. Mirrors the backend's `parse_transient_token`; the
 * signature is not verified because the payload is only used for display and
 * the number it carries is already masked.
 */
export const decodeTransientTokenCard = (
    jwt: string,
): { maskedNumber: string; cardType: string } => {
    const payload = decodeJwtPayload<TransientTokenPayload>(jwt);
    const number = payload?.content?.paymentInformation?.card?.number;
    return {
        maskedNumber: number?.maskedValue ?? "",
        cardType: number?.detectedCardTypes?.[0] ?? "",
    };
};

/**
 * When a transient token JWT was issued and when it expires, in epoch
 * milliseconds on CyberSource's clock, or `null` if it lacks either claim.
 * CyberSource rejects a payment authorized with an expired token with a 400.
 */
export const decodeTransientTokenValidity = (
    jwt: string,
): { issuedAt: number; expiresAt: number } | null => {
    const payload = decodeJwtPayload<TransientTokenPayload>(jwt);
    const iat = payload?.iat;
    const exp = payload?.exp;
    if (typeof iat !== "number" || typeof exp !== "number") {
        return null;
    }
    return { issuedAt: iat * 1000, expiresAt: exp * 1000 };
};

let flexSdkPromise: Promise<void> | null = null;

/**
 * Dynamically load the Flex Microform SDK script with SRI and crossOrigin.
 * Uses a singleton promise to prevent duplicate script injection when
 * multiple widgets (e.g. split pay) call this concurrently.
 */
export const loadFlexSdk = (url: string, integrity: string): Promise<void> => {
    const win = getWindow();
    if (win?.Flex) {
        return Promise.resolve();
    }
    if (flexSdkPromise) {
        return flexSdkPromise;
    }
    const document = getDocument();
    if (!document) {
        return Promise.reject(new Error("Document not available"));
    }
    flexSdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = url;
        script.integrity = integrity;
        script.crossOrigin = "anonymous";
        script.onload = () => {
            resolve();
        };
        script.onerror = () => {
            flexSdkPromise = null;
            reject(new Error(`Failed to load Flex SDK from ${url}`));
        };
        document.head.appendChild(script);
    });
    return flexSdkPromise;
};

/**
 * Create a capture context for Microform v2. Called when the user
 * selects the credit card payment method, before rendering the fields.
 */
export const createCaptureContext =
    async (): Promise<ICaptureContextResponse> => {
        return fetchJson<ICaptureContextResponse>(
            "/api/cybersource-microform/capture-context/",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    [CSRF_HEADER]: await getCSRFToken(),
                },
            },
        );
    };
